import {
  Flex,
  Input,
  PasswordInput,
  PasswordInputProps,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { InfoCircle, Lock } from "iconsax-react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface IProps extends PasswordInputProps {
  name: string;
  hasTooltip?: boolean;
}

export default function PasswordInputControlled({
  name,
  hasTooltip = false,
  ...props
}: IProps) {
  const { control } = useFormContext();
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const MOCK_REQUIRED_TOOLTIP_TEXT = [
    t("global.password_requirements.lower_case"),
    t("global.password_requirements.upper_case"),
    t("global.password_requirements.number"),
    t("global.password_requirements.special_character"),
    t("global.password_requirements.min_length"),
  ];

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Input.Wrapper dir="column" withAsterisk w={props.w}>
          <Input.Label required w={"100%"}>
            <Flex justify={"space-between"} align={"center"} w={"100%"}>
              {props.label}
              {hasTooltip ? (
                <Tooltip
                  maw={"448px"}
                  w="100%"
                  label={
                    <Flex mr="8px" direction={"column"} gap="8px">
                      <Text size="sm" color="white">
                        {t("global.password_requirements.title")}
                      </Text>
                      <Flex wrap={"wrap"} columnGap={"24px"} rowGap={"8px"}>
                        {MOCK_REQUIRED_TOOLTIP_TEXT.map((text, index) => (
                          <RequiredTooltipText
                            value={text}
                            key={`${text}-${index}`}
                          />
                        ))}
                      </Flex>
                    </Flex>
                  }
                >
                  <Flex mr="8px">
                    <InfoCircle size="22" color={theme.colors.basic[4]} />
                  </Flex>
                </Tooltip>
              ) : null}
            </Flex>
          </Input.Label>
          <PasswordInput
            icon={<Lock size="24" color={theme.colors.basic[5]} />}
            error={!!error?.message ? t(error.message) : ""}
            visibilityToggleIcon={({ reveal }) =>
              reveal ? (
                <p>{t("global.input.password.hide_password")}</p>
              ) : (
                <p>{t("global.input.password.show_password")}</p>
              )
            }
            fz={{ base: 14, 991: 16 }}
            {...field}
            {...props}
            withAsterisk={false}
            label={""}
          />
        </Input.Wrapper>
      )}
    />
  );
}

const RequiredTooltipText = ({ value }: { value: string }) => {
  const theme = useMantineTheme();
  return (
    <Flex gap="4px" align={"center"} maw="fit-content">
      <InfoCircle size="24" color={theme.colors.basic[4]} />
      <Text size="sm" color="white" maw="fit-content">
        {value}
      </Text>
    </Flex>
  );
};
